import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBusinessPlan } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import TooltipLabel from "./ui/TooltipLabel";
import { 
  ArrowLeft,
  Target,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  Percent,
  DollarSign
} from "lucide-react";
import { formatCurrency as formatCurrencyUtil } from '../utils/sharedCalculations';
import { usePlanningYears } from '../hooks/usePlanningYears';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlanningDetails = () => {
  const navigate = useNavigate();
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [keyAssumptions, setKeyAssumptions] = useState(null);
  const [planningCoverage, setPlanningCoverage] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const planningData = usePlanningYears(currentPlan?.id);
  const { years } = planningData;

  const formatCurrency = (amount, compact = false) => {
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, compact);
  };

  // Load planning details
  const loadPlanningDetails = async () => {
    if (!currentPlan?.id || !years || years.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const latestYear = Math.max(...years);
      const startYear = Math.min(...years);
      
      // Fetch financial data
      const financialResponse = await axios.get(
        `${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${latestYear}`
      );
      
      const keyMetrics = financialResponse.data.financial_statements?.key_metrics || {};
      const income = financialResponse.data.financial_statements?.income_statement || {};
      
      // Fetch advanced calculations
      const breakEvenResponse = await axios.get(`${BACKEND_URL}/api/v3/calculations/break-even/${currentPlan.id}`);
      const cashFlowResponse = await axios.get(`${BACKEND_URL}/api/v3/calculations/cash-flow/${currentPlan.id}`);
      
      const breakEvenData = breakEvenResponse.data?.break_even_analysis || {};
      const cashFlowData = cashFlowResponse.data?.summary || {};
      
      // Fetch cost data
      const costResponse = await axios.get(`${BACKEND_URL}/api/v3/cost-items/${currentPlan.id}`);
      const costData = costResponse.data || [];
      const monthlyCosts = costData.reduce((sum, cost) => sum + (cost.amount || 0), 0);
      
      // Calculate growth rate
      let revenueGrowth = 0;
      if (years.length >= 2) {
        const prevYear = years[years.length - 2];
        const prevFinancialResponse = await axios.get(
          `${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${prevYear}`
        );
        const prevIncome = prevFinancialResponse.data.financial_statements?.income_statement || {};
        if (prevIncome.revenue && income.revenue) {
          revenueGrowth = ((income.revenue - prevIncome.revenue) / prevIncome.revenue) * 100;
        }
      }
      
      // Set key assumptions
      setKeyAssumptions([
        {
          label: 'Revenue Growth Rate',
          value: `${revenueGrowth.toFixed(1)}%`,
          icon: TrendingUp
        },
        {
          label: 'Gross Margin',
          value: `${(keyMetrics.gross_margin_percent || 0).toFixed(1)}%`,
          icon: Percent
        },
        {
          label: 'Monthly Burn Rate',
          value: formatCurrency(monthlyCosts),
          icon: DollarSign
        },
        {
          label: 'Cash Runway',
          value: `${Math.round(cashFlowData.average_cash_runway_months || 0)} months`,
          icon: Clock
        },
        {
          label: 'Break-Even Timeline',
          value: `${Math.round(breakEvenData.break_even_months || 0)} months`,
          icon: Target
        }
      ]);
      
      // Calculate planning coverage
      const financialDataExists = income.revenue > 0;
      const costDataExists = costData.length > 0;
      const cashFlowExists = cashFlowData.average_cash_runway_months > 0;
      const breakEvenExists = breakEvenData.break_even_months > 0;
      
      setPlanningCoverage([
        {
          area: 'Revenue Model',
          percentage: financialDataExists ? 100 : 0,
          status: financialDataExists ? 'complete' : 'incomplete'
        },
        {
          area: 'Cost Structure',
          percentage: costDataExists ? 100 : 0,
          status: costDataExists ? 'complete' : 'incomplete'
        },
        {
          area: 'Cash Flow Planning',
          percentage: cashFlowExists ? 100 : 0,
          status: cashFlowExists ? 'complete' : 'incomplete'
        },
        {
          area: 'Break-Even Analysis',
          percentage: breakEvenExists ? 100 : 0,
          status: breakEvenExists ? 'complete' : 'incomplete'
        },
        {
          area: 'Strategic Initiatives',
          percentage: 80,
          status: 'in-progress'
        }
      ]);
      
      // Calculate milestones
      const breakEvenMonths = breakEvenData.break_even_months || 12;
      const breakEvenYear = startYear + Math.floor(breakEvenMonths / 12);
      
      // Find first profitable year
      let profitabilityYear = null;
      for (const year of years) {
        const yearResponse = await axios.get(
          `${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${year}`
        );
        const yearIncome = yearResponse.data.financial_statements?.income_statement || {};
        if (yearIncome.net_income > 0) {
          profitabilityYear = year;
          break;
        }
      }
      
      setMilestones([
        {
          id: 'start',
          title: 'Plan Start',
          date: `Year ${startYear}`,
          status: 'completed',
          description: 'Financial planning initiated'
        },
        {
          id: 'breakeven',
          title: 'Break-Even',
          date: `Month ${breakEvenMonths} (Year ${breakEvenYear})`,
          status: breakEvenMonths <= 12 ? 'in-progress' : 'planned',
          description: 'Revenue covers all costs'
        },
        {
          id: 'profitability',
          title: 'Profitability',
          date: profitabilityYear ? `Year ${profitabilityYear}` : 'TBD',
          status: profitabilityYear === startYear ? 'completed' : 'planned',
          description: 'Positive net income achieved'
        },
        {
          id: 'growth',
          title: 'Growth Phase',
          date: `Year ${startYear + 2}`,
          status: 'planned',
          description: 'Expansion and scaling'
        },
        {
          id: 'completion',
          title: 'Plan Completion',
          date: `Year ${Math.max(...years)}`,
          status: 'planned',
          description: 'Strategic goals achieved'
        }
      ]);
      
    } catch (error) {
      console.error('Error loading planning details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanningDetails();
  }, [currentPlan?.id, years]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-48 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/planning-dashboard')}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Planning Dashboard
        </Button>
        <TooltipLabel
          label={<h1 className="text-3xl font-bold text-slate-900">Planning Details</h1>}
          tooltip="Detailed view of planning coverage, milestones, and key assumptions"
          asChild
        />
        <p className="text-slate-600 mt-1">
          Planning coverage, milestones, and key assumptions for {currentPlan?.company_name || currentPlan?.name}
        </p>
      </div>

      {/* Key Assumptions */}
      {keyAssumptions && keyAssumptions.length > 0 && (
        <Card className="border-t-4 border-t-purple-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <Target className="h-5 w-5 mr-2 text-purple-600" />
                  Key Assumptions
                </CardTitle>
              }
              tooltip="Key Assumptions are the foundational variables that drive your financial projections. These 5 metrics form the basis of all calculations: (1) Revenue Growth Rate - expected year-over-year increase in sales, (2) Gross Margin - profit percentage after COGS, (3) Monthly Burn Rate - cash spent per month, (4) Cash Runway - months until cash depletion, (5) Break-Even Timeline - when revenue equals costs. Example: 25% revenue growth + 60% gross margin + $50K burn = 12-month runway. Changes to these assumptions cascade through all projections. Review quarterly and adjust as actual performance deviates. Document reasoning behind each assumption for stakeholder confidence."
              asChild
            />
            <CardDescription>Critical planning parameters driving projections</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {keyAssumptions.map((assumption, index) => {
                const Icon = assumption.icon;
                return (
                  <TooltipLabel
                    key={index}
                    label={
                      <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="bg-purple-100 p-2 rounded-full">
                            <Icon className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">{assumption.label}</p>
                            <p className="text-lg font-bold text-purple-600">{assumption.value}</p>
                          </div>
                        </div>
                      </div>
                    }
                    tooltip={`${assumption.label}: ${assumption.value}`}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Planning Coverage */}
      {planningCoverage && planningCoverage.length > 0 && (
        <Card className="border-t-4 border-t-green-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Planning Coverage
                </CardTitle>
              }
              tooltip="Planning Coverage tracks completeness across 5 essential planning areas: (1) Revenue Model (100%) - products, pricing, sales forecast defined, (2) Cost Structure (100%) - all COGS, operating expenses, and overhead mapped, (3) Cash Flow Planning (100%) - inflows/outflows tracked monthly, (4) Break-Even Analysis (100%) - point where revenue covers costs calculated, (5) Strategic Initiatives (%) - growth plans, expansions, partnerships documented. Each area contributes 20% to overall score. Complete (100%) = green checkmark, In Progress (1-99%) = yellow clock, Incomplete (0%) = red warning. Target 100% across all areas before seeking funding or making major decisions. Incomplete areas create blind spots in planning."
              asChild
            />
            <CardDescription>Completeness of your financial plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {planningCoverage.map((area, index) => (
                <TooltipLabel
                  key={index}
                  label={
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-700">{area.area}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-bold text-slate-900">{area.percentage}%</span>
                          {area.status === 'complete' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : area.status === 'in-progress' ? (
                            <Clock className="h-4 w-4 text-yellow-500" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all duration-500 ${
                            area.status === 'complete' ? 'bg-green-500' : 
                            area.status === 'in-progress' ? 'bg-yellow-500' : 
                            'bg-red-500'
                          }`}
                          style={{ width: `${area.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  }
                  tooltip={`${area.area}: ${area.percentage}% complete`}
                />
              ))}
              
              {/* Overall Progress */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-slate-900">Overall Completion:</span>
                  <span className="text-2xl font-bold text-green-600">
                    {Math.round(planningCoverage.reduce((sum, a) => sum + a.percentage, 0) / planningCoverage.length)}%
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestones Timeline */}
      {milestones && milestones.length > 0 && (
        <Card className="border-t-4 border-t-blue-500">
          <CardHeader>
            <TooltipLabel
              label={
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                  Planning Milestones Timeline
                </CardTitle>
              }
              tooltip="Planning Milestones Timeline visualizes your business journey with 5 key checkpoints: (1) Plan Start (green) - planning phase begins, baseline established, (2) Break-Even Point (yellow/blue) - monthly revenue equals monthly costs (critical milestone), (3) Profitability (blue) - first month/year with positive net income, (4) Growth Phase (blue) - scaling operations, expanding market presence, (5) Plan Completion (blue) - strategic goals achieved, 5-year plan fulfilled. Status colors: Green = Completed, Yellow = In Progress (current focus), Blue = Planned (future). Example timeline: Start Y1M1 → Break-even Y1M12 → Profitable Y2M6 → Growth Y3 → Complete Y5. Use to communicate progress to investors, align team on priorities, and track strategic execution."
              asChild
            />
            <CardDescription>Key dates and strategic checkpoints</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute top-8 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-600"></div>
              
              {/* Milestones */}
              <div className="grid grid-cols-5 gap-4 relative">
                {milestones.map((milestone, index) => (
                  <TooltipLabel
                    key={milestone.id}
                    label={
                      <div className="flex flex-col items-center">
                        {/* Milestone Marker */}
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center z-10 ${
                          milestone.status === 'completed' ? 'bg-green-500' :
                          milestone.status === 'in-progress' ? 'bg-yellow-500' :
                          'bg-blue-500'
                        } shadow-lg`}>
                          {milestone.status === 'completed' ? (
                            <CheckCircle className="h-8 w-8 text-white" />
                          ) : milestone.status === 'in-progress' ? (
                            <Clock className="h-8 w-8 text-white" />
                          ) : (
                            <Target className="h-8 w-8 text-white" />
                          )}
                        </div>
                        
                        {/* Milestone Info */}
                        <div className="mt-4 text-center">
                          <h4 className="font-bold text-sm text-slate-900">{milestone.title}</h4>
                          <p className="text-xs text-slate-600 mt-1">{milestone.date}</p>
                          <p className="text-xs text-slate-500 mt-1">{milestone.description}</p>
                          <Badge 
                            variant={
                              milestone.status === 'completed' ? "success" :
                              milestone.status === 'in-progress' ? "warning" :
                              "default"
                            }
                            className="mt-2 text-xs"
                          >
                            {milestone.status.replace('-', ' ').toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                    }
                    tooltip={`${milestone.title}: ${milestone.description} (${milestone.date})`}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PlanningDetails;
